import { NgClass } from "@angular/common";
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { TranslateModule } from "@ngx-translate/core";
import { TilbyGesturesDirective } from "@tilby/tilby-ui-lib/directives/tilby-gestures";
import { TilbyCurrencyPipe } from "@tilby/tilby-ui-lib/pipes/tilby-currency";
import { PriceChangeDescriptionObj, TilbyPriceChangePipe } from "@tilby/tilby-ui-lib/pipes/tilby-price-change";
import { SaleItemWithMenu } from "@tilby/tilby-ui-lib/components/tilby-order";
import { Departments, SalesItemsPriceChanges } from "tilby-models";

@Component({
    selector: "app-shop-cart-row-v2",
    templateUrl: "./shop-cart-row-v2.component.html",
    standalone: true,
    imports: [NgClass, MatIconModule, TranslateModule, TilbyCurrencyPipe, TilbyGesturesDirective, TilbyPriceChangePipe],
    host: { class: "tw-block" }
})
export class ShopCartRowV2Component implements OnChanges, OnDestroy {
    protected readonly Math = Math;

    @Input({ required: true }) saleItem!: SaleItemWithMenu;
    @Input() department?: Departments;
    @Input() priceChangeDescriptions!: PriceChangeDescriptionObj;
    @Input() isHidePrices = false;
    @Input() ingredientsRemovalAffectsPrice = false;
    @Input() expanded = false;
    @Input() selected = false;
    @Input() grouped = false;
    @Input() disabled = false;
    @Input() flash = false;
    @Input() sent = false;

    @Output() rowClick = new EventEmitter<void>();
    @Output() rowMouseDown = new EventEmitter<void>();
    @Output() toggleExpanded = new EventEmitter<void>();
    @Output() swipeLeft = new EventEmitter<void>();
    @Output() swipeRight = new EventEmitter<void>();
    @Output() longPress = new EventEmitter<void>();
    @Output() removePriceChange = new EventEmitter<SalesItemsPriceChanges>();

    protected flashFading = false;
    private flashFadingTimeout?: ReturnType<typeof setTimeout>;

    ngOnChanges(changes: SimpleChanges) {
        if (changes.flash && changes.flash.previousValue && !changes.flash.currentValue) {
            this.flashFading = true;
            clearTimeout(this.flashFadingTimeout);
            this.flashFadingTimeout = setTimeout(() => this.flashFading = false, 2500);
        }
    }

    ngOnDestroy() {
        clearTimeout(this.flashFadingTimeout);
    }

    protected get hasDetails(): boolean {
        const item = this.saleItem;
        return !!((item.type === 'refund' && item.refund_cause_description) || item.not_discountable || item.notes || item.variations?.length || item.ingredients?.length || (item.price_changes?.length && !this.hasDiscount) || item.$menuIndex);
    }

    protected get hasExpandedContent(): boolean {
        const item = this.saleItem;
        return !!((item.type === 'refund' && item.refund_cause_description) || item.not_discountable || item.notes || item.variations?.length || item.ingredients?.length || item.price_changes?.length);
    }

    protected get hasDiscount(): boolean {
        return !!this.saleItem.price_changes?.some((priceChange) => priceChange.type === 'discount_fix' || priceChange.type === 'discount_perc');
    }

    protected get vatLabel(): string | undefined {
        return this.department?.vat?.code || (this.department?.vat?.value !== undefined ? this.department.vat.value + "%" : undefined);
    }

    private gestureHandled = false;

    protected gestureDone(): void {
        this.gestureHandled = true;
    }

    protected resetGesture(): void {
        this.gestureHandled = false;
    }

    protected onToggle(event: Event): void {
        event.stopPropagation();

        if (this.gestureHandled) {
            this.gestureHandled = false;
            return;
        }

        this.toggleExpanded.emit();
    }
}
